// React Query hooks for the `field/forms/` API — see src/services/fieldForms.ts.
// Mirrors the existing hook pattern (src/api/cmTables.ts).

import { useMutation, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import {
  createForm,
  deleteForm,
  getForm,
  listForms,
  listPublishedFormsForTable,
  PaginatedResponse,
  publishForm,
  updateForm
} from "../services/fieldForms";
import { CreateFormPayload, FieldForm, ListFormsParams, UpdateFormPayload } from "../types/fieldForm";

const KEYS = {
  all: ["field-forms"] as const,
  list: (params?: ListFormsParams & { form_group_id?: string }) => ["field-forms", "list", params ?? {}] as const,
  detail: (uuid: string | null) => ["field-forms", "detail", uuid] as const,
  tableForms: (tableSlug: string | null) => ["field-forms", "table", tableSlug] as const
};

/** List forms (optionally filtered by table / status / form_group_id). */
export const useListForms = (
  params?: ListFormsParams & { form_group_id?: string },
  options?: Omit<UseQueryOptions<PaginatedResponse<FieldForm>, Error>, "queryKey" | "queryFn">
): UseQueryResult<PaginatedResponse<FieldForm>, Error> => {
  return useQuery<PaginatedResponse<FieldForm>, Error>({
    queryKey: KEYS.list(params),
    queryFn: async () => (await listForms(params)).data,
    ...options
  });
};

/** Retrieve one form version. */
export const useForm = (
  uuid: string | null,
  options?: Omit<UseQueryOptions<FieldForm, Error>, "queryKey" | "queryFn">
): UseQueryResult<FieldForm, Error> => {
  return useQuery<FieldForm, Error>({
    queryKey: KEYS.detail(uuid),
    enabled: uuid !== null,
    queryFn: async () => {
      if (!uuid) throw new Error("No form uuid");
      return (await getForm(uuid)).data;
    },
    ...options
  });
};

/** Published forms bound to a table (for `mode:"new"` relation pickers). */
export const usePublishedFormsForTable = (
  tableSlug: string | null,
  options?: Omit<UseQueryOptions<PaginatedResponse<FieldForm>, Error>, "queryKey" | "queryFn">
): UseQueryResult<PaginatedResponse<FieldForm>, Error> => {
  return useQuery<PaginatedResponse<FieldForm>, Error>({
    queryKey: KEYS.tableForms(tableSlug),
    enabled: tableSlug !== null,
    queryFn: async () => {
      if (!tableSlug) throw new Error("No tableSlug");
      return (await listPublishedFormsForTable(tableSlug)).data;
    },
    ...options
  });
};

/** Create a draft (or a new version of an existing form_group_id). */
export const useCreateForm = () => {
  const queryClient = useQueryClient();
  return useMutation<FieldForm, Error, CreateFormPayload>({
    mutationFn: async (payload) => (await createForm(payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all })
  });
};

/** Edit a draft (schema is a full replacement). */
export const useUpdateForm = () => {
  const queryClient = useQueryClient();
  return useMutation<FieldForm, Error, { uuid: string; payload: UpdateFormPayload }>({
    mutationFn: async ({ uuid, payload }) => (await updateForm(uuid, payload)).data,
    onSuccess: (form) => {
      queryClient.invalidateQueries({ queryKey: KEYS.detail(form.uuid) });
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    }
  });
};

/** Publish a draft (validate + freeze). */
export const usePublishForm = () => {
  const queryClient = useQueryClient();
  return useMutation<FieldForm, Error, string>({
    mutationFn: async (uuid) => (await publishForm(uuid)).data,
    onSuccess: (form) => {
      queryClient.invalidateQueries({ queryKey: KEYS.detail(form.uuid) });
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    }
  });
};

/** Archive / delete a form. */
export const useDeleteForm = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (uuid) => {
      await deleteForm(uuid);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all })
  });
};
